import type * as React from "react"
import { lazy, Suspense, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Copy, WrapText } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { MermaidPlaceholder } from "./mermaid-placeholder"

// Lazy-load MermaidBlock (imports the heavy mermaid library ~300KB)
const MermaidBlock = lazy(() =>
  import("./mermaid-block").then((m) => ({ default: m.MermaidBlock }))
)
const LazyPrismHighlighter = lazy(() =>
  import("./lazy-prism-highlighter").then((m) => ({ default: m.LazyPrismHighlighter }))
)

export type CodeBlockProps = {
  inline?: boolean
  className?: string
  children?: React.ReactNode
  isStreaming?: boolean
  sessionId?: string
  onMermaidBlockUpdate?: (oldCode: string, newCode: string) => Promise<void>
  canEditMermaid?: boolean
}

const LANGUAGE_TEXTS: Record<string, string> = {
  php: "PHP",
  csharp: "C#",
  cpp: "C++",
  css: "CSS",
  html: "HTML",
  json: "JSON",
  sql: "SQL",
  xml: "XML",
  latex: "LaTeX",
  yaml: "YAML",
  toml: "TOML",
  "objective-c": "Objective-C",
  fsharp: "F#",
  vb: "Visual Basic .NET",
  coffeescript: "CoffeeScript",
  actionscript: "ActionScript",
  nasm: "NASM",
  json5: "JSON5",
} as const

const PENDING_SUFFIX = "_pending"

export function CodeBlock({
  inline,
  className,
  children,
  isStreaming = false,
  sessionId,
  onMermaidBlockUpdate,
  canEditMermaid,
}: CodeBlockProps) {
  const { t } = useTranslation()
  const [wrapLines, setWrapLines] = useState(true)

  const rawCode = Array.isArray(children) ? children.join("") : String(children)
  const codeString = rawCode.replace(/^\s+/, "").replace(/\n$/, "")
  const fixedCodeString = codeString.trimStart()
  const match = /language-(\w+)/.exec(className || "")
  // A still-open fence is rewritten upstream (downgradeUnclosedFenceWhileStreaming
  // in answer-parser.tsx) to `<lang>_pending` so this component can skip the
  // expensive parts of rendering (Prism tokenization, Mermaid diagrams) until
  // the fence actually closes, instead of redoing that work on every chunk.
  const isPending = Boolean(match && match[1].toLowerCase().endsWith(PENDING_SUFFIX))
  let baseLanguage: string | undefined
  if (match) {
    baseLanguage = isPending ? match[1].slice(0, -PENDING_SUFFIX.length) : match[1]
  }
  const languageText = baseLanguage
    ? (LANGUAGE_TEXTS[baseLanguage] ?? baseLanguage.charAt(0).toUpperCase() + baseLanguage.slice(1))
    : ""

  const isMermaid = match && match[1].toLowerCase() === "mermaid"
  if (!inline && isMermaid) {
    return (
      <Suspense fallback={<MermaidPlaceholder />}>
        <MermaidBlock
          key={`mermaid-${fixedCodeString.length}-${fixedCodeString.slice(0, 100)}`}
          canEdit={canEditMermaid}
          code={fixedCodeString}
          isStreaming={isStreaming}
          sessionId={sessionId}
          onMermaidBlockUpdate={onMermaidBlockUpdate}
        />
      </Suspense>
    )
  }

  // Pending mermaid blocks show code with a hint until the fence closes
  const isMermaidPending = match && match[1].toLowerCase() === "mermaid_pending"

  if (!inline && match) {
    return (
      <section className="bg-sidebar my-2 rounded-[6px] border">
        <header className="flex items-center justify-between px-[14px] pt-2">
          <p className="text-muted-foreground !m-0 text-xs font-medium">{languageText}</p>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger
                className={cn(
                  buttonVariants({
                    size: "icon",
                    variant: "icon",
                    className: "group size-7 hover:bg-transparent",
                  })
                )}
                onClick={() => setWrapLines(!wrapLines)}
              >
                <WrapText
                  className={cn("size-4", wrapLines ? "text-foreground" : "text-muted-foreground")}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="top" sideOffset={5}>
                <p>
                  {wrapLines ? t("answer.disable_text_wrapping") : t("answer.enable_text_wrapping")}
                </p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                className={cn(
                  buttonVariants({
                    size: "icon",
                    variant: "icon",
                    className: "group size-7 hover:bg-transparent",
                  })
                )}
                onClick={async () => {
                  await navigator.clipboard.writeText(fixedCodeString)
                  toast.success(t("answer.code_copied"))
                }}
              >
                <Copy className="text-foreground size-4" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="top" sideOffset={5}>
                <p>{t("answer.copy")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </header>
        {isMermaidPending && (
          <p className="text-muted-foreground mx-[14px] mt-1 flex items-center gap-2 text-xs">
            <Spinner className="size-3" />
            {t("answer.mermaid.diagram_pending", "Diagram will render when complete")}
          </p>
        )}
        {isPending ? (
          <pre
            className={cn(
              "text-foreground overflow-x-auto px-[14px] py-2 text-sm",
              // Mirrors LazyPrismHighlighter's own wrapLongLines behavior
              // (whiteSpace: pre-wrap vs pre) so the wrap-lines toggle in
              // the header still has an effect while a fence is pending.
              wrapLines ? "break-words whitespace-pre-wrap" : "whitespace-pre"
            )}
          >
            <code>{fixedCodeString}</code>
          </pre>
        ) : (
          <Suspense
            fallback={
              <pre className="text-foreground overflow-x-auto px-[14px] py-2 text-sm">
                <code>{fixedCodeString}</code>
              </pre>
            }
          >
            <LazyPrismHighlighter
              className="!border-none !bg-transparent [&_.linenumber]:min-w-[3em] [&_.linenumber]:pl-4 [&_.prism-code]:m-0 [&_.prism-code]:py-2"
              code={fixedCodeString}
              language={match[1]}
              wrapLongLines={wrapLines}
            />
          </Suspense>
        )}
      </section>
    )
  } else {
    return <code className={className}>{children}</code>
  }
}
